/* eslint-disable no-unused-expressions */
import type { History } from "history";
import { commentsLayerId, FROM_MAP_ENGINE, MAPBOX_MAX_ZOOM, MAPBOX_MIN_ZOOM, selectedLayerAreaSelectId, systemLayerIds } from "@iventis/map-engine/src/bridge/constants";
import { MapStoreEvent, MapStoreEventType, ObjectUpdate } from "@iventis/map-engine/src/types/events-store";
import { AssetOptions, CompositionMapObject, EngineInterpreterOptions } from "@iventis/map-engine/src/types/internal";
import { MapModuleLayer, MapStore, MappingEngine } from "@iventis/map-engine/src/types/store-schema";
import { Content } from "@iventis/translations/content/typed-content";
import {
    queryParams,
    isValidUuid,
    xstateDevTools,
    EventStream,
    getAssetToCachedAsset,
    decimalAdjust,
    API_KEY_QUERY_PARAM_KEY,
    PROJECT_ID_QUERY_PARAM_KEY,
    replaceItemInArray,
    coordinatesToString,
    ZERO_TIME_SPAN,
    useKeyboardShortcut,
} from "@iventis/utilities";
import { generateRoute, MapEvents, ModeEvents } from "@iventis/map-engine";
import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { MapboxEngine } from "@iventis/map-engine/src/bridge/mapbox/engine-mapbox";
import { UnrealEngine } from "@iventis/map-engine/src/bridge/unreal/engine-unreal";
import { multipleModelsGetter } from "@iventis/api-helpers-mapping";
import { transformRequest } from "@iventis/map-engine/src/bridge/mapbox/tile-requests";
import { findNode, TreeBrowserStateInterface } from "@iventis/tree-browser";
import { toast } from "@iventis/toasts/src/toast";
import { isCompositionValid } from "@iventis/map-engine/src/utilities/geojson-helpers";
import { MapGlobalState } from "@iventis/map-engine/src/state/map.state.types";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { mapFontStackUrl } from "@iventis/map-engine/src/map-fonts";
import { compositionMapObjectToMapObjectUpdate, objectUpdateToNode } from "@iventis/map-engine/src/utilities/converters";
import { DataField } from "@iventis/domain-model/model/dataField";
import { StyleType } from "@iventis/domain-model/model/styleType";
import area from "@turf/area";
import length from "@turf/length";
import { feature } from "@turf/helpers";
import { isAnalysisLayer, isAreaLayer } from "@iventis/map-engine/src/utilities/state-helpers";
import { RoutePlannerEvent, routePlannerEventStream } from "@iventis/route-planner";
import { RouteWaypoint } from "@iventis/domain-model/model/routeWaypoint";
import { TimeSpan } from "@iventis/types";
import { assetCacheService, assetUrlGetter, bustCacheIds, getIventisEnvironmentData, mappingApi, multipleAssetUrlGetter } from "@iventis/api/src/api";
import { translate } from "@iventis/translations/translation";
import { authSlice } from "@iventis/permissions/src/auth.slice";
import { UserScope } from "@iventis/domain-model/model/userScope";
import { SearchResultGetters } from "@iventis/search";
import { createLog, incrementMonitoringCount } from "@iventis/observability-and-monitoring";
import {
    getMapObjectRouteTimeSystemDataField,
    getMapObjectLengthSystemDataField,
    getMapObjectAreaSystemDataField,
    getMapObjectQuantityOfModelsOnLineSystemDataField,
    getQuantityOfModelsOnALine,
    getMapObjectCoordinatesSystemDataField,
    getMapObjectIconRotationSystemDataField,
} from "@iventis/datafields";
import { LocalGeoJsonObject, SelectedMapObject } from "@iventis/map-types";
import { MetricName } from "@iventis/domain-model/model/metricName";
import { getMapObjectSitemap } from "@iventis/map-objects";
import { checkModeIsPresent } from "@iventis/map-engine/src/machines/mode-machine.helpers";
import { UnrealEngineErrors } from "@iventis/map-engine/src/bridge/unreal/engine-unreal.types";
import { undoableMapObjectProperties } from "@iventis/map-engine/src/utilities/undo-redo-helpers";
import { DigitalTwinLicence } from "@iventis/domain-model/model/digitalTwinLicence";
import { assetService } from "./services/asset-service";
import { setPosition, createMapObjectWithGeoJson, setMode, composeNewObjects, searchMapObjectsByArea } from "./map.helpers";
import {
    createObjects,
    deleteObjects,
    getGeoJson,
    GetGeoJsonResponse,
    patchMapComment,
    uploadGeometry,
    patchMapObjectsDataFieldValues,
    getMapCommentFeature,
    setUserConnectedToDigitalTwin,
} from "./state/map.slice.thunks";
import { RootDispatch, RootState, rootStore, Store } from "./state/root.store";
import { map } from "./state/map.slice";
import { sidebar } from "./state/sidebar.slice";
import { PopoverComponents, SidebarContent } from "./state/sidebar.slice.types";
import { createSearchResultGroups } from "./search-result.helpers";
import { FROM_MAP_ROOT, FROM_MAP_UNDO_REDO } from "./map-constants";
import {
    checkIfMapObjectHasWhat3WordsValue,
    updateSelectedMapObjectsWithNoWhat3WordsValue,
    getWhat3WordsValueForObjectUpdates,
    patchWhat3WordsValueToNullRemote,
} from "./map-container-what3words";
import { routeWaypointService } from "./services/route-waypoint-service";
import { MapContainerUpdateEvent, MapContainerUpdateEvents } from "./map-container-reducer";
import { getMapObjectAndCommentBoundsThunk } from "./state/map.slice.get-map-thunks";
import { MapObjectUndoRedoChange, MapUndoRedoContextType, useMapUndoRedoContext, exctractChangeInObjectUpdate, MapObjectUndoRedoEntry } from "./map-undo-redo-context";
import { refreshTerrainSignatures } from "./basemap.helpers";

export const instantiateEngine = (
    mappingEngine: MappingEngine,
    mapModuleStore: MapStore,
    globalStore: Store,
    eventStream: EventStream<ModeEvents | MapEvents>,
    container: HTMLElement,
    mbxAccessToken: string,
    unrealIpAddress: (_digitalTwinLicence: DigitalTwinLicence) => Promise<string>,
    unrealGetApiKey: () => Promise<string>,
    getAttributeListItems: EngineInterpreterOptions["getAttributeListItems"],
    isMobileUser: boolean,
    showReconnecting: (isConnecting: boolean) => void,
    exitMappingEngine: (error: UnrealEngineErrors) => void,
    setWebGLSupported: (supported: boolean) => void
) => {
    const mapState = globalStore.getState().mapReducer as MapGlobalState;
    const { user } = globalStore.getState().auth;
    const sharedOptions: EngineInterpreterOptions = {
        store: mapModuleStore,
        container,
        eventStream,
        preview: false,
        modifierKeys: ["Control", "Meta", "Shift"],
        assetOptions,
        devTools: xstateDevTools,
        getAttributeListItems,
        user: { id: user.userId, isMobileUser },
        isExternalUser: user.userScope === UserScope.External,
        routeApiFunctions: {
            routeFinder: generateRoute,
            getWaypoints: async (objectId) => {
                // If a route planner is already open, we need to clear it before opening a new one
                if (globalStore.getState().mapReducer.showRoutePlanner) {
                    routePlannerEventStream.next({
                        type: RoutePlannerEvent.LoadingWaypoints,
                        payload: null,
                    });
                }
                rootStore.dispatch(map.actions.setShowRoutePlanner(true));
                const waypoints = await routeWaypointService.getRouteWaypoints(objectId, mapState.mapModule.mapId);
                return waypoints;
            },
            maxWaypoints: 25,
        },
    };
    switch (mappingEngine) {
        case MappingEngine.Mapbox:
            return new MapboxEngine({
                ...sharedOptions,
                mbxAccessToken,
                transformRequest: (url) => {
                    const mapState = globalStore.getState().mapReducer as MapGlobalState;
                    return transformRequest(
                        url,
                        mapState.signatures,
                        queryParams[API_KEY_QUERY_PARAM_KEY],
                        mapState.mapModule.terrain3D.value.customTerrains,
                        queryParams[PROJECT_ID_QUERY_PARAM_KEY]
                    );
                },
                maxZoom: MAPBOX_MAX_ZOOM,
                minZoom: MAPBOX_MIN_ZOOM,
                fontStackUrl: mapFontStackUrl,
                // If there is no saved map view then use the map object bounds
                useMapObjectBounds: mapState.savedMapViewDefault == null,
                // Needs to be enabled for recording of the canvas
                preserveDrawingBuffer: true,
                cypressWindowName: "IVENTIS_ENGINE_TESTING_FUNCTIONS",
                setWebGLSupported,
            });
        case MappingEngine.Unreal:
            return new UnrealEngine({
                ...sharedOptions,
                getIpAddress: unrealIpAddress,
                getApiKey: unrealGetApiKey,
                projectId: (globalStore.getState() as RootState).auth.user.projectId,
                apiURL: getIventisEnvironmentData().apiUrl,
                showReconnecting,
                exitMappingEngine,
            });
        default:
            throw new Error("Mapping engine not recognised");
    }
};

export const mapInterpreterEventHandler = (
    globalStore: Store,
    dispatch: RootDispatch,
    undoRedo: { current: MapUndoRedoContextType | undefined },
    treeBrowserContext: TreeBrowserStateInterface<Node>,
    setSavedMapViewTooltipDebounce: () => void,
    history: History<unknown>,
    isMobileUser: boolean,
    updateMapContainer: (event: MapContainerUpdateEvents) => void,
    createAbortControllerForLevelChange: () => AbortController,
    showMapBackgrondErrorToast: (mapBackgroundName: string) => void
) => async (event: MapStoreEvent) => {
    switch (event.eventType) {
        case MapStoreEventType.SET_MODE:
            setMode(event.payload.mode);
            break;
        case MapStoreEventType.EXIT_ANALYSIS_MODE:
            dispatch(sidebar.actions.setSidebarContent(SidebarContent.LAYERS));
            break;
        case MapStoreEventType.SET_POSITION:
            setPosition(event.payload.mapPostion, event.payload.setLevel, history);
            if (event.payload.commentCanvasPosition != null) {
                dispatch(map.actions.updateSelectedCommentPosition(event.payload.commentCanvasPosition));
            }
            break;
        case MapStoreEventType.COMPOSE_NEW_OBJECTS:
            composeNewObjects(event.payload.objectIds);
            break;
        case MapStoreEventType.REQUEST_GEOMETRY: {
            const layers = globalStore.getState().mapReducer.mapModule.layers.value;
            const { payload } = (await dispatch(getGeoJson({ objects: event.payload.objects, mapId: globalStore.getState().mapReducer.mapModule.mapId, layers }))) as {
                payload: GetGeoJsonResponse[];
            };

            // If the requested object has a mode of transport property, set the route planner to that mode of transport
            const modeOfTransport = payload?.[0]?.feature.properties.modeOfTransport;
            if (payload?.length === 1 && modeOfTransport) {
                routePlannerEventStream.next({ type: RoutePlannerEvent.SetModeOfTransport, payload: { modeOfTransport } });
                // Get the route info from the system data fields on the object
                const { projectDataFields } = globalStore.getState().mapReducer.mapModule;
                const durationProperty = getMapObjectRouteTimeSystemDataField(projectDataFields)?.id;
                const duration: TimeSpan = durationProperty
                    ? (payload[0].feature.properties[getMapObjectRouteTimeSystemDataField(projectDataFields)?.id] as TimeSpan)
                    : ZERO_TIME_SPAN;
                const distanceProperty = getMapObjectLengthSystemDataField(projectDataFields)?.id;
                const distance = distanceProperty ? (payload[0].feature.properties[getMapObjectLengthSystemDataField(projectDataFields)?.id] as number) : 0;
                // Tell the route planner the route info (duration and distance)
                routePlannerEventStream.next({ type: RoutePlannerEvent.SetRouteInfo, payload: { routeInfo: { duration, distance } } });
            }

            if (payload?.length > 0) {
                checkIfMapObjectHasWhat3WordsValue(payload, layers, dispatch);
            }
            break;
        }
        case MapStoreEventType.SET_OBJECTS: {
            // Get the map state
            const mapState = globalStore.getState().mapReducer;
            const { mapId, projectDataFields, layers, localToRemoteMapObjectIdsMap, mode, geoJSON } = mapState.mapModule;

            // Filter out any map objects which we don't care about (i.e. system layer objects which aren't stored anywhere outside the engine)
            let mapObjectsChanges = event.payload.filter(({ layerId }) => !systemLayerIds.includes(layerId));

            // Update the system data fields of the map objects
            const updateMapObjectSystemDataFieldsResponse = updateMapObjectSystemDataFields(projectDataFields, mapObjectsChanges, layers.value);
            mapObjectsChanges = updateMapObjectSystemDataFieldsResponse.updatedMapObjects;

            // Update the map objects sitemap property
            mapObjectsChanges = updateMapObjectsWithSitemap(mapObjectsChanges, mapState);

            // To set the local geoJson
            const storeChanges: CompositionMapObject[] = [];
            // To handle valid map object updates and creations
            const mapObjectsToCreate: ObjectUpdate[] = [];
            const mapObjectsToUpdate: ObjectUpdate[] = [];
            // To store undo/redo changes
            const undoRedoChange: MapObjectUndoRedoChange[] = [];
            // To update analysis objects selection property
            const analysisObjects: ObjectUpdate[] = [];

            // Loop through each map object change to decide what to do with it
            mapObjectsChanges.forEach((change) => {
                const previousLocalGeoJson = geoJSON?.value[change.layerId]?.find((obj) => obj.objectId === change.objectId);
                const previousObject = previousLocalGeoJson ? localGeoJsonObjectToObjectUpdate(previousLocalGeoJson) : undefined;

                const newProperties = change.properties;
                const newObject = { ...change, properties: newProperties };

                // All changes get added to the store (even if they have invalid geometry)
                storeChanges.push(createMapObjectWithGeoJson(newProperties, change.geometry, change.waypoints));

                const newGeometryIsValid = isCompositionValid(change.geometry);
                if (newGeometryIsValid) {
                    const isMapObjectCreation = isValidUuid(change.layerId) && localToRemoteMapObjectIdsMap.value[change.objectId] === undefined;
                    if (isMapObjectCreation) {
                        mapObjectsToCreate.push(newObject);
                    }
                    const isMapObjectUpdate = isValidUuid(change.layerId) && localToRemoteMapObjectIdsMap.value[change.objectId] !== undefined;
                    if (isMapObjectUpdate) {
                        mapObjectsToUpdate.push(newObject);
                    }
                    // As long as we're not in composition mode, push the change to undoRedo
                    const isComposing = checkModeIsPresent(mode.value, "composition");
                    if (!isComposing) {
                        const change = exctractChangeInObjectUpdate(previousObject, newObject, () => getAllUndoableMapObjectProperties(projectDataFields));
                        if (change && change !== "equal") {
                            undoRedoChange.push(change);
                        }
                    }
                }

                const isAnalysisObject = isAnalysisLayer(change.layerId);
                if (isAnalysisObject) {
                    analysisObjects.push(newObject);
                }
            });

            // Store the new local geoJson
            dispatch(map.actions.setLocalGeoJson(storeChanges));

            // Store the undo/redo changes
            if (undoRedoChange.length > 0) {
                undoRedo.current?.push({ type: "mapObject", operation: "update", change: undoRedoChange });
            }

            // Handle map objects to be created on the server
            if (mapObjectsToCreate.length > 0) {
                dispatch(createObjects(mapObjectsToCreate));
                // Add nodes and select them in the tree browser
                const nodesToAdd = mapObjectsToCreate.map((creation) => objectUpdateToNode(creation, projectDataFields));
                if (nodesToAdd.length > 0) treeBrowserContext.addNodesLocally(nodesToAdd);
                treeBrowserContext.toggleSelectNodes(nodesToAdd, { from: FROM_MAP_ENGINE, undoable: false });

                if (mapObjectsToCreate.length === 1) {
                    // Update all map objects What3Words value if the layer has a What3Words datafield.
                    // We only do this in the local store as the object may not exist remotely yet and the back end will populate this value as it is null server side.
                    getWhat3WordsValueForObjectUpdates(mapObjectsToCreate, layers.value, dispatch);
                    // Set the route info for route objects
                    updateInfoForRouteObjects(mapObjectsToCreate, projectDataFields);
                }
                incrementMonitoringCount({ name: MetricName.MAP_OBJECT_CREATED, quantity: mapObjectsToCreate.length, resourceId: mapId });
            }

            // Handle map objects to be updated on the server
            if (mapObjectsToUpdate.length > 0) {
                dispatch(uploadGeometry(mapObjectsToUpdate));

                // Update the system data fields of the map objects
                const { areSystemDataFieldsUpdated } = updateMapObjectSystemDataFieldsResponse;
                if (areSystemDataFieldsUpdated) {
                    dispatch(
                        patchMapObjectsDataFieldValues({ mapObjects: createMapObjectsWithSystemDataFields(mapObjectsToUpdate, projectDataFields), skipReflectLocalSource: true })
                    );
                }

                if (mapObjectsToUpdate.length === 1) {
                    // Update all map objects What3Words value if the layer has a What3Words datafield.
                    // This is updated remotely as it is a single object update otherwise the back end would never update its value.
                    getWhat3WordsValueForObjectUpdates(mapObjectsToUpdate, layers.value, dispatch, true);
                    // Update the route info if the object is a route
                    updateInfoForRouteObjects(mapObjectsToUpdate, projectDataFields);
                } else {
                    // By setting the what 3 words to null remotely, it will allow the back end to update the value in an eventually consistent mannor to match any geom changes.
                    // This also updates the local store
                    patchWhat3WordsValueToNullRemote(mapObjectsToUpdate, layers.value, dispatch);
                }
            }

            // If analysis objects are updated, ensure they are the only analysis objects shown as selected
            if (analysisObjects?.length > 0) {
                dispatch(map.actions.setMapObjectsSelected({ newSelection: analysisObjects, addToUndoStack: false }));
            }
            break;
        }
        case MapStoreEventType.DESELECT_LAYERS: {
            treeBrowserContext.toggleSelectNodes([], { from: FROM_MAP_ROOT, ignoreExistingSelection: true });
            break;
        }
        case MapStoreEventType.MAP_MOVE_START: {
            refreshTerrainSignatures(globalStore.getState().mapReducer.signatures);
            break;
        }
        case MapStoreEventType.AREA_SELECT_FINISHED: {
            if (event.payload.object.layerId !== selectedLayerAreaSelectId) {
                throw Error("Only area select objects should be passed to this event");
            }
            const { mapModule } = (globalStore.getState() as RootState).mapReducer;
            const { mapId, currentLevel } = mapModule;
            dispatch(map.actions.removeObjects([event.payload.object.objectId]));
            /** Get the objects within the area and select them */
            const { geometry } = event.payload.object;
            const layers = mapModule.layers.value.filter((layer) => layer.selected);
            const coordinates = geometry.coordinates as number[][][];
            // At least 4 points in the polygon geometry
            if (!event.payload.cancelled) {
                if (coordinates.length !== 0 && coordinates[0].length >= 4) {
                    const dateFilter = mapModule.datesFilter.value.filter ? { day: mapModule.datesFilter.value.day, time: mapModule.datesFilter.value.time } : undefined;
                    // Search by area
                    const { selectedMapObjects } = await searchMapObjectsByArea(layers, coordinates as number[][][], mapId, currentLevel, dateFilter);
                    // Inform the tree browser of the resulting selection
                    const objectNodes = prepareObjectsForTreeBrowserSelection(selectedMapObjects, treeBrowserContext);
                    treeBrowserContext.toggleSelectNodes(objectNodes, { from: FROM_MAP_ROOT, ignoreExistingSelection: true });
                } else {
                    toast.info({
                        title: translate(Content.common.alert),
                        message: translate(Content.map2.controls.area_select_invalid),
                    });
                }
            }
            break;
        }
        case MapStoreEventType.DRAG_START: {
            updateMapContainer({ type: MapContainerUpdateEvent.HIDE_POPUP });
            break;
        }
        case MapStoreEventType.DELETE_COMPOSITION: {
            const currentState = globalStore.getState().mapReducer;
            const localToRemote = currentState.mapModule.localToRemoteMapObjectIdsMap.value;
            const { local, remote } = (event.payload as SelectedMapObject[]).reduce<{ local: string[]; remote: SelectedMapObject[] }>(
                (acc, { objectId, layerId }) =>
                    localToRemote[objectId] ? { ...acc, remote: [...acc.remote, { objectId, layerId }] } : { ...acc, local: [...acc.local, objectId] },
                {
                    local: [],
                    remote: [],
                }
            );
            if (remote.length) {
                dispatch(deleteObjects({ mapObjects: remote, modifyStore: true }));
            }
            if (local.length) {
                dispatch(map.actions.removeObjects(local));
            }
            // If there was an initial composition object, we know it was a continue drawing operation,
            // so register an update to revert it to it's original form before we register the deletion
            if ("initialCompositionObject" in event && event.initialCompositionObject !== undefined) {
                const latestCompositionObject = event.payload[0];
                const change = exctractChangeInObjectUpdate(
                    compositionMapObjectToMapObjectUpdate(event.initialCompositionObject),
                    compositionMapObjectToMapObjectUpdate(latestCompositionObject),
                    () => getAllUndoableMapObjectProperties(currentState.mapModule.projectDataFields)
                );
                // If it has changed form it's orginal state, register the change
                if (change && change !== "equal") {
                    undoRedo.current?.push({
                        type: "mapObject",
                        operation: "update",
                        change: [change],
                    });
                }
            }
            undoRedo.current?.registerDelete(event.payload);
            treeBrowserContext.removeNodesLocally(
                event.payload.map(({ objectId }) => objectId),
                { from: FROM_MAP_ENGINE }
            );
            break;
        }
        case MapStoreEventType.DRAWING_FINISHED: {
            if (globalStore.getState().auth.userPreferences.showAddAnotherObjectToLayer) {
                dispatch(map.actions.setDrawingToolTip("click-to-draw-again"));
            }
            // Push the creation/update to the undo stack
            const operation = event.initialFeatures ? "update" : "create";
            const entry: MapObjectUndoRedoEntry = { type: "mapObject" as const, operation, items: [], change: [] };
            if (entry.operation === "update") {
                entry.change = event.features.reduce<MapObjectUndoRedoChange[]>((acc, f) => {
                    const previousFeature = event.initialFeatures.find((pf) => pf.objectId === f.objectId);
                    const previousObject = previousFeature
                        ? {
                              objectId: previousFeature.objectId,
                              layerId: previousFeature.layerId,
                              geometry: previousFeature.geojson.geometry,
                              waypoints: previousFeature.waypoints,
                              properties: previousFeature.geojson.properties,
                          }
                        : undefined;

                    const nextObject: ObjectUpdate = {
                        objectId: f.objectId,
                        layerId: f.layerId,
                        geometry: f.geojson.geometry,
                        waypoints: f.waypoints,
                        properties: f.geojson.properties,
                    };
                    const change = exctractChangeInObjectUpdate(previousObject, nextObject, () =>
                        getAllUndoableMapObjectProperties(globalStore.getState().mapReducer.mapModule.projectDataFields)
                    );
                    if (change && change !== "equal") {
                        acc.push(change);
                    }
                    return acc;
                }, []);

                // If there are no changes to make, break early
                if (!entry.change?.length) {
                    break;
                }
            }
            if (entry.operation === "create") {
                entry.items = event.features.map((f) => compositionMapObjectToMapObjectUpdate(f));
                // If there are no items to create, break early
                if (!entry.items?.length) {
                    break;
                }
            }
            undoRedo.current?.push(entry);
            break;
        }
        case MapStoreEventType.MAP_MOVE_END: {
            const hasMapGotDefaultView = globalStore.getState().mapReducer.savedMapViewDefault != null;
            if (globalStore.getState().mapReducer.isMapPanningAfterSearch) {
                dispatch(map.actions.setIsMapPanningAfterSearch(false));
                if (!hasMapGotDefaultView) {
                    // Wait 1 second
                    await new Promise((r) => setTimeout(r, 1000));
                    dispatch(map.actions.setSavedMapViewTooltip(true));
                }
            }
            if (!hasMapGotDefaultView && event.payload.zoom > 11) {
                setSavedMapViewTooltipDebounce();
            }
            break;
        }
        case MapStoreEventType.OBJECT_PLACEMENT_STARTED: {
            // When object placement starts, we want to prevent pressing of the duplication button again, or undo/ redo
            dispatch(map.actions.setIsPlacingGeometry(true));
            break;
        }
        case MapStoreEventType.OBJECT_PLACEMENT_FINISHED: {
            // When object placement starts, we want to prevent pressing of the duplication button again, or undo/ redo
            dispatch(map.actions.setIsPlacingGeometry(false));
            break;
        }
        case MapStoreEventType.LEVEL_CHANGED: {
            history.push({ search: new URLSearchParams({ level: `${event.payload.level}` }).toString() });
            const abortController = createAbortControllerForLevelChange();
            dispatch(
                getMapObjectAndCommentBoundsThunk({
                    mapId: globalStore.getState().mapReducer.mapModule.mapId,
                    level: event.payload.level?.toString(),
                    signal: abortController.signal,
                })
            );

            // Clear our undo redo stack
            undoRedo.current?.clear();

            break;
        }
        case MapStoreEventType.DELETE_OBJECTS_REQUEST: {
            if (globalStore.getState().mapReducer.mapModule.mapObjectsSelected?.value?.length > 0) {
                updateMapContainer({ type: MapContainerUpdateEvent.SHOW_DELETE_OBJECT_MODAL });
            }
            break;
        }
        case MapStoreEventType.UPDATE_ROUTE_WAYPOINTS: {
            const waypoints = event.payload.waypoints.map<RouteWaypoint>((w, index) => ({
                name: w.coordinates ? coordinatesToString(w.coordinates) : undefined,
                ...w,
                order: index + 1,
            }));
            // Update the route planner
            routePlannerEventStream.next({ type: RoutePlannerEvent.SetRouteWaypoints, payload: { waypoints } });
            break;
        }
        case MapStoreEventType.SET_EDITING_ROUTE: {
            dispatch(map.actions.setShowRoutePlanner(event.payload));
            if (event.payload && "waypoints" in event.object) {
                routePlannerEventStream.next({
                    type: RoutePlannerEvent.SetRouteWaypoints,
                    payload: { waypoints: event.object.waypoints?.map((w, index) => ({ ...w, order: index + 1 })) ?? [] },
                });
            }
            if (event.payload && "geojson" in event.object && event.object.geojson.properties?.modeOfTransport) {
                routePlannerEventStream.next({ type: RoutePlannerEvent.SetModeOfTransport, payload: { modeOfTransport: event.object.geojson.properties?.modeOfTransport } });
                // Get the route info from the system data fields on the object
                const { projectDataFields } = globalStore.getState().mapReducer.mapModule;
                const durationProperty = getMapObjectRouteTimeSystemDataField(projectDataFields)?.id;
                const duration: TimeSpan = durationProperty
                    ? (event.object.geojson.properties?.[getMapObjectRouteTimeSystemDataField(projectDataFields)?.id] as TimeSpan)
                    : ZERO_TIME_SPAN;
                const distanceProperty = getMapObjectLengthSystemDataField(projectDataFields)?.id;
                const distance = distanceProperty ? (event.object.geojson.properties?.[getMapObjectLengthSystemDataField(projectDataFields)?.id] as number) : 0;
                // Tell the route planner the route info (duration and distance)
                routePlannerEventStream.next({ type: RoutePlannerEvent.SetRouteInfo, payload: { routeInfo: { duration, distance } } });
            }

            break;
        }
        case MapStoreEventType.SET_COMMENT_GEOMETRY: {
            const state = globalStore.getState();
            const updatedComment = {
                ...event.payload,
                properties: {
                    ...event.payload.properties,
                    userId: state.auth.user.userId,
                    sitemapId: getMapObjectSitemap(event.payload.geometry, event.payload.properties.level, state.mapReducer.sitemaps, state.mapReducer.sitemapConfigs),
                },
            };

            if (state.mapReducer.mapModule.localToRemoteMapObjectIdsMap.value[updatedComment.properties.id] != null) {
                dispatch(patchMapComment(updatedComment));
            } else {
                if (!state.auth.doesUserHaveProfileImage) {
                    dispatch(authSlice.actions.generateUserIcon());
                }
                // Comment is yet to save to the server, so we just update locally
                dispatch(
                    map.actions.setLocalGeoJson([
                        {
                            geojson: updatedComment,
                            objectId: updatedComment.properties.id,
                            layerId: commentsLayerId,
                            level: updatedComment.properties.level,
                            waypoints: undefined,
                            sitemapId: updatedComment.properties.sitemapId,
                        },
                    ])
                );
                // Ensure we show the comments so it is visible to the user
                dispatch(map.actions.setHideComments(false));
            }
            break;
        }
        case MapStoreEventType.DELETE_MAP_COMMENT: {
            dispatch(map.actions.setCommentsSelected([]));
            dispatch(map.actions.removeObjects([event.commentId]));
            break;
        }
        case MapStoreEventType.SELECT_MAP_COMMENT: {
            const mapModuleState = globalStore.getState().mapReducer.mapModule;

            const previousSelection = mapModuleState.commentsSelected.value?.[0];

            // Select the comment
            dispatch(map.actions.setCommentsSelected(event.payload == null ? [] : [event.payload]));

            // If a comment is already selected, set the image back to the user icon and order back to 0
            if (previousSelection != null) {
                const geojson = mapModuleState.geoJSON.value[commentsLayerId]?.find((c) => c.objectId === previousSelection?.commentId);
                if (geojson != null) {
                    if (mapModuleState.localToRemoteMapObjectIdsMap.value[previousSelection.commentId] == null) {
                        dispatch(map.actions.removeObjects([previousSelection.commentId]));
                    } else {
                        dispatch(
                            map.actions.setLocalGeoJson([
                                {
                                    ...geojson,
                                    layerId: commentsLayerId,
                                    geojson: { ...geojson.feature, properties: { ...geojson.feature.properties, imageId: geojson.feature.properties.userId, order: 0 } },
                                },
                            ])
                        );
                    }
                }
            }
            // If no comment is selected, return early
            if (event.payload == null) {
                break;
            }
            dispatch(map.actions.setDrawingModifier("none"));
            treeBrowserContext.toggleSelectNodes([], { from: FROM_MAP_ENGINE, ignoreExistingSelection: true });
            await dispatch(getMapCommentFeature({ commentId: event.payload?.commentId, focus: true, panTo: isMobileUser, existsOnServer: !event.isNewComment }));
            if (!event.isNewComment) {
                incrementMonitoringCount({ name: MetricName.COMMENT_SELECTED, resourceId: event.payload.commentId });
            }
            break;
        }
        case MapStoreEventType.SET_MAP_OBJECTS_SELECTED: {
            // Remove system layer objects
            const layers = globalStore.getState().mapReducer.mapModule.layers.value;
            const nonSystemLayerMapObjects = event.payload.mapObjects.reduce((cum, curr) => {
                const layer = layers.find(({ id }) => id === curr.layerId);
                if (layer == null) {
                    throw new Error(`Layer id ${curr.layerId} doesn't exist`);
                }
                return systemLayerIds.includes(layer?.id) ? cum : [...cum, curr];
            }, [] as SelectedMapObject[]);
            // Inform the map slice of the new selection (for both analysis and user layers)
            const analysisObjects = nonSystemLayerMapObjects.filter(({ layerId }) => isAnalysisLayer(layerId));
            if (analysisObjects?.length) {
                dispatch(map.actions.setMapObjectsSelected({ newSelection: analysisObjects, addToUndoStack: event.payload.from !== FROM_MAP_UNDO_REDO }));
            } else {
                // Inform the tree browser of the new selection
                const objectNodes = prepareObjectsForTreeBrowserSelection(nonSystemLayerMapObjects, treeBrowserContext);
                treeBrowserContext.toggleSelectNodes(objectNodes, {
                    from: event.payload.from,
                    ignoreExistingSelection: true,
                    affectedNodeTypes: [NodeType.MapObject],
                });
                // Get all map objects which are local and have
                updateSelectedMapObjectsWithNoWhat3WordsValue(layers, globalStore.getState().mapReducer.mapModule.geoJSON.value, nonSystemLayerMapObjects, dispatch);
            }
            break;
        }
        case MapStoreEventType.SELECT_ANALYSIS_LAYER: {
            treeBrowserContext.toggleSelectNodes([], { from: event.from, ignoreExistingSelection: true });
            dispatch(map.actions.updateLayerSelection({ selection: [event.layerId] }));
            break;
        }
        case MapStoreEventType.ENTER_COMMENTS_MODE: {
            treeBrowserContext.toggleSelectNodes([], { from: FROM_MAP_ENGINE, ignoreExistingSelection: true });
            break;
        }
        case MapStoreEventType.CANCEL_COMMENT: {
            const localToRemote = globalStore.getState().mapReducer.mapModule.localToRemoteMapObjectIdsMap.value;
            // If the comment is not remote, when we cancel we need to remove it from the local geo json
            if (localToRemote[event.selectedCommentId] == null) {
                dispatch(map.actions.removeObjects([event.selectedCommentId]));
            }
            break;
        }
        case MapStoreEventType.UPDATE_SELECTED_COMMENT_POSITION: {
            if (event.payload != null) {
                dispatch(map.actions.updateSelectedCommentPosition(event.payload.canvasPosition));
            }
            break;
        }
        case MapStoreEventType.UPDATE_COMMENTS_DRAWING_MODE: {
            dispatch(map.actions.setCommentsDrawingMode(event.mode));
            break;
        }
        case MapStoreEventType.SET_DRAWING_MODIFIER: {
            dispatch(map.actions.setDrawingModifier(event.payload));
            break;
        }
        case MapStoreEventType.SET_CAMERA_MODE: {
            dispatch(map.actions.setCameraMode({ value: event.payload, stamp: false }));
            break;
        }
        case MapStoreEventType.SET_VENUE: {
            dispatch(map.actions.setVenue(event.payload));
            break;
        }
        case MapStoreEventType.SET_USER_CONNECTED_TO_DIGITAL_TWIN: {
            dispatch(setUserConnectedToDigitalTwin({ connected: event.payload }));
            break;
        }
        case MapStoreEventType.EXIT_DIGITAL_TWIN: {
            dispatch(map.actions.exitDigitalTwin());
            break;
        }
        case MapStoreEventType.MAP_BACKGROUND_ERRORED: {
            const mapState = globalStore.getState().mapReducer;
            const mapBackgroundFailedToLoad = mapState.mapBackgrounds.find(({ id }) => id === event.payload.mapBackgroundId);
            dispatch(map.actions.failedToLoadMapBackground(mapState.mapModule.selectedMapBackgroundId));
            showMapBackgrondErrorToast(mapBackgroundFailedToLoad.name);
            createLog("Map background errored", "error", event.payload);
            break;
        }
        case MapStoreEventType.SPLIT_LINE: {
            undoRedo.current.clear();
            break;
        }
        default:
            break;
    }
};

export const createMapPopoverProps = (component: PopoverComponents, mapId: string, searchResultGetters: SearchResultGetters) =>
    component === PopoverComponents.SEARCH ? { searchResultGroups: createSearchResultGroups(mapId, searchResultGetters) } : null;

export const useMapKeyShortcuts = (undoRedo: ReturnType<typeof useMapUndoRedoContext>) => {
    const tryUndo = () => {
        undoRedo?.undo();
    };

    const tryRedo = () => {
        undoRedo?.redo();
    };

    // Undo shortcuts
    useKeyboardShortcut("z", { ctrl: true, shift: false }, tryUndo);

    // Redo shortcuts
    useKeyboardShortcut("z", { ctrl: true, shift: true }, tryRedo);
    useKeyboardShortcut("y", { ctrl: true, shift: false }, tryRedo);
};

export function localGeoJsonObjectToObjectUpdate(object: LocalGeoJsonObject): ObjectUpdate {
    return {
        geometry: object.feature.geometry,
        layerId: object.feature.properties.layerid,
        objectId: object.objectId,
        properties: object.feature.properties,
        waypoints: object.waypoints,
    };
}

export const assetOptions: AssetOptions = {
    // Route only accepts uuids, but some basemap ids may pass into here so we must filter them out
    assetUrlGetter: async (id) => (isValidUuid(id) ? assetUrlGetter(id) : null),
    multipleAssetUrlGetter: async (ids) => multipleAssetUrlGetter(ids),
    multipleModelsGetter: async (ids) => multipleModelsGetter(ids, mappingApi),
    isAssetSdf: async (id) => (await assetCacheService.get(id, async () => getAssetToCachedAsset(id, assetService.getAsset)))?.metaData?.sdf ?? false,
    bustCacheIds,
};

/** Adds length and area values to given map object and returns a boolean which is true if a system datafield has been updated */
export function updateMapObjectSystemDataFields(
    projectDataFields: DataField[],
    mapObjectsUpdates: ObjectUpdate[] = [],
    mapLayers: MapModuleLayer[] = []
): { updatedMapObjects: ObjectUpdate[]; areSystemDataFieldsUpdated: boolean } {
    let areSystemDataFieldsUpdated = false;
    const updatedMapObjects = mapObjectsUpdates.map((mapObject) => {
        const layer = mapLayers.find((layer) => layer.id === mapObject.layerId);
        // Rotation needs to be set to default value if undefined otherwise error is thrown by API
        const rotation = mapObject.properties.rotation ?? { x: 0, y: 0, z: 0 };
        // Don't want to update values till object drawn is valid
        const compositionValid = isCompositionValid(mapObject.geometry);

        // If layer is area then calculate the area of the polygon
        if (isAreaLayer(layer) && compositionValid) {
            const areaSystemDataField = getMapObjectAreaSystemDataField(projectDataFields);
            if (areaSystemDataField) {
                areSystemDataFieldsUpdated = true;
                // Set the area to id of area system datafield, value rounded to 2dp
                return { ...mapObject, properties: { ...mapObject.properties, [areaSystemDataField.id]: decimalAdjust("round", area(mapObject.geometry), -2), rotation } };
            }
        }

        // If layer is a line or line of models calculate the length of the line
        if ((layer?.styleType === StyleType.Line || layer?.styleType === StyleType.LineModel) && compositionValid) {
            const lengthSystemDataField = getMapObjectLengthSystemDataField(projectDataFields);
            if (lengthSystemDataField) {
                areSystemDataFieldsUpdated = true;

                const newMapObject = {
                    ...mapObject,
                    properties: {
                        ...mapObject.properties,
                        // Set the length to id of length system datafield, value rounded to 2dp
                        [lengthSystemDataField.id]: decimalAdjust("round", length(feature(mapObject.geometry), { units: "meters" }), -2),
                        rotation,
                    },
                };

                if (layer.styleType === StyleType.LineModel) {
                    const quantitySystemDataField = getMapObjectQuantityOfModelsOnLineSystemDataField(projectDataFields);
                    newMapObject.properties[quantitySystemDataField.id] = getQuantityOfModelsOnALine(
                        { type: "Feature", geometry: mapObject.geometry, properties: mapObject.properties },
                        layer.lineModelStyle
                    );
                }

                return newMapObject;
            }
        }

        if ((layer?.styleType === StyleType.Point || layer?.styleType === StyleType.Icon || layer?.styleType === StyleType.Model) && compositionValid) {
            const coordinateSystemDataField = getMapObjectCoordinatesSystemDataField(projectDataFields);
            if (coordinateSystemDataField) {
                areSystemDataFieldsUpdated = true;
                return {
                    ...mapObject,
                    properties: {
                        ...mapObject.properties,
                        // Set the coordinate to the coordinate system datafield
                        [coordinateSystemDataField.id]: coordinatesToString(mapObject.geometry.coordinates as number[]),
                        rotation,
                    },
                };
            }
        }

        // If the layer is neither a line or area then just return the original map object
        return { ...mapObject, properties: { ...mapObject.properties, rotation } };
    });
    return { updatedMapObjects, areSystemDataFieldsUpdated };
}

/** Parses ObjectUpdates to MapObject for updating the datafield values for length and area */
export function createMapObjectsWithSystemDataFields(mapObjectUpdates: ObjectUpdate[], projectDataFields: DataField[]): Pick<MapObject, "id" | "layerId" | "dataFieldValues">[] {
    const areaDataField = getMapObjectAreaSystemDataField(projectDataFields);
    const lengthDataField = getMapObjectLengthSystemDataField(projectDataFields);
    const coordsDataField = getMapObjectCoordinatesSystemDataField(projectDataFields);
    const routeTimeDataField = getMapObjectRouteTimeSystemDataField(projectDataFields);
    const quantityDataField = getMapObjectQuantityOfModelsOnLineSystemDataField(projectDataFields);
    const iconRotationDataField = getMapObjectIconRotationSystemDataField(projectDataFields);
    return mapObjectUpdates.map((object) => {
        // Get either the are or length value
        const areaValue = object.properties[areaDataField?.id];
        const lengthValue = object.properties[lengthDataField?.id];
        const coordsValue = object.properties[coordsDataField?.id];
        const routeTimeValue = object.properties[routeTimeDataField?.id];
        const quantityValue = object.properties[quantityDataField?.id];
        const iconRotationValue = object.properties[iconRotationDataField?.id];
        const dataFieldValues = {};
        // If value is present add to dataFieldValues
        if (areaValue) {
            dataFieldValues[areaDataField.id] = areaValue;
        }
        if (lengthValue) {
            dataFieldValues[lengthDataField.id] = lengthValue;
        }
        if (coordsValue) {
            dataFieldValues[coordsDataField.id] = coordsValue;
        }
        if (routeTimeValue) {
            dataFieldValues[routeTimeDataField.id] = routeTimeValue;
        }
        if (quantityValue) {
            dataFieldValues[quantityDataField.id] = quantityValue;
        }

        // It is possible that the icon rotation value is 0 which is falsy so do a proper check here
        if (iconRotationValue != null) {
            dataFieldValues[iconRotationDataField.id] = iconRotationValue;
        }

        return { id: object.objectId, layerId: object.layerId, dataFieldValues };
    });
}

/** Prepares the selected objects for tree browser selction by ensuring it exists inside its' layer */
const prepareObjectsForTreeBrowserSelection = (mapObjects: SelectedMapObject[], treeBrowserContext: TreeBrowserStateInterface<Node>) => {
    const { layerNodesToUpdate: layersToUpdate, objectNodes } = mapObjects.reduce(
        (acc, curr) => {
            const tree = treeBrowserContext.currentState?.tree?.childNodes;
            const objectNode = findNode(tree ?? [], curr.objectId);
            if (objectNode == null) {
                let layerNode = acc.layerNodesToUpdate.find((l) => l.id === curr.layerId);
                let layerAlreadyAdded = true;
                // If the layer has not already been added, find it from within the tree
                if (layerNode == null) {
                    layerNode = findNode(tree, curr.layerId);
                    layerAlreadyAdded = false;
                }
                // Give temporary name so sorting works correctly, layer is expanded the name will be updated correctly
                const basicObjectNode = { id: curr.objectId, name: "", type: NodeType.MapObject, parentId: curr.layerId, childNodes: [] } as Node;
                // Append the basic object node to the layers' children
                const newLayerNode = {
                    ...layerNode,
                    childNodes: [...layerNode.childNodes, basicObjectNode],
                };
                return {
                    // Upsert the layer with it's new object inside
                    layerNodesToUpdate: layerAlreadyAdded ? replaceItemInArray(acc.layerNodesToUpdate, newLayerNode) : [...acc.layerNodesToUpdate, newLayerNode],
                    objectNodes: [...acc.objectNodes, basicObjectNode],
                };
            }
            return { ...acc, objectNodes: [...acc.objectNodes, objectNode] };
        },
        { layerNodesToUpdate: [] as Node[], objectNodes: [] as Node[] }
    );
    // For each layer of the selected objects, update them locally with the selected objects basic nodes as children, so the tree browser can select them
    layersToUpdate.forEach((l) => treeBrowserContext.updateNodeLocally(l, undefined, true, false));
    return objectNodes;
};

/** If the route planner is open, this function sends it an event to update the route info (distance and duration) */
export const updateInfoForRouteObjects = (objects: ObjectUpdate[], projectDataFields?: DataField[]) => {
    if (objects.length === 1 && objects[0].properties.modeOfTransport != null) {
        const durationProperty = getMapObjectRouteTimeSystemDataField(projectDataFields)?.id;
        const lengthProperty = getMapObjectLengthSystemDataField(projectDataFields)?.id;
        routePlannerEventStream.next({
            type: RoutePlannerEvent.SetRouteInfo,
            payload: {
                routeInfo: {
                    duration: objects[0].properties[durationProperty] as TimeSpan,
                    distance: objects[0].properties[lengthProperty] as number,
                },
            },
        });
    }
};

/** Will assign a sitemapId to each map object */
export function updateMapObjectsWithSitemap(mapObjects: ObjectUpdate[], mapState: MapGlobalState): ObjectUpdate[] {
    const { sitemapConfigs, sitemaps } = mapState;
    return mapObjects.map((mapObject) => {
        const sitemapId = getMapObjectSitemap(mapObject.geometry, mapObject.properties.level, sitemaps, sitemapConfigs);
        return { ...mapObject, properties: { ...mapObject.properties, sitemapId } };
    });
}

/** Gets all the map object properties that are undoable, such as route duration, route distance, rotation, level, etc */
function getAllUndoableMapObjectProperties(projectDataFields: DataField[]) {
    const durationProperty = getMapObjectRouteTimeSystemDataField(projectDataFields)?.id;
    const distanceProperty = getMapObjectLengthSystemDataField(projectDataFields)?.id;
    const iconRotationProperty = getMapObjectIconRotationSystemDataField(projectDataFields)?.id;
    return [...undoableMapObjectProperties, durationProperty, distanceProperty, iconRotationProperty];
}
